import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CreateMessage from "@/pages/CreateMessage";
import Confirmation from "@/pages/Confirmation";
import MessageView from "@/pages/MessageView";
import Privacy from "@/pages/Privacy";
import ConsentScreen from "@/pages/ConsentScreen";
import AdminDashboard from "@/pages/AdminDashboard";
import ScheduledDeliveries from "@/pages/ScheduledDeliveries";
import MagicLinkCallback from "@/pages/MagicLinkCallback";
import StatusPage from "@/pages/StatusPage";
import Invoices from "@/pages/Invoices";
import CookieBanner from "@/components/CookieBanner";
import { LanguageProvider } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      console.log("[AUTH] Checking authentication via /auth/me");
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        console.log("[AUTH] Authenticated as:", userData.email);
        setUser(userData);
      } else {
        console.log("[AUTH] Not authenticated (status:", response.status, ")");
        setUser(null);
      }
    } catch (error) {
      console.error("[AUTH] Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  useEffect(() => {
    // Skip auth check if we're in the middle of an OAuth callback —
    // AuthCallback will handle authentication and call setUser itself.
    if (window.location.hash?.includes('session_id=')) {
      console.log("[AUTH] OAuth callback detected — skipping checkAuth");
      setLoading(false);
      return;
    }
    // Also skip for magic link callback
    if (window.location.pathname === '/auth/magic') {
      console.log("[AUTH] Magic link callback detected — skipping checkAuth");
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  return { user, setUser, loading, logout, checkAuth };
};

// Protected Route wrapper — also checks consent
const ProtectedRoute = ({ children, user, loading, setUser }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    // Save intended destination so post-login flow can return the user here
    try {
      const target = `${location.pathname}${location.search || ""}`;
      if (target && target !== "/") {
        sessionStorage.setItem("bizou_redirect_after_login", target);
      }
    } catch (e) {
      // sessionStorage may be unavailable (private mode) — fail silently
    }
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check consent
  if (!user.consent_given) {
    return (
      <ConsentScreen
        user={user}
        onConsent={() => setUser({ ...user, consent_given: true })}
      />
    );
  }

  return children;
};

// App Router component
function AppRouter() {
  const location = useLocation();
  const { user, setUser, loading, logout, checkAuth } = useAuth();

  // Check for session_id in URL fragment (from Google OAuth)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <>
      <Routes>
        <Route 
          path="/" 
          element={<LandingPage user={user} logout={logout} />} 
        />
        <Route 
          path="/privacy" 
          element={<Privacy />} 
        />
        <Route 
          path="/status" 
          element={<StatusPage />} 
        />
        <Route 
          path="/invoices" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <Invoices user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/auth/magic" 
          element={<MagicLinkCallback setUser={setUser} />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <Dashboard user={user} logout={logout} setUser={setUser} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/create" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <CreateMessage user={user} logout={logout} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/confirmation" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <Confirmation user={user} logout={logout} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/message/:token" 
          element={<MessageView user={user} />} 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <AdminDashboard user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/scheduled" 
          element={
            <ProtectedRoute user={user} loading={loading} setUser={setUser}>
              <ScheduledDeliveries user={user} />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <CookieBanner />
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
    <div className="App">
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: "'Manrope', sans-serif"
          }
        }}
      />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
    </LanguageProvider>
  );
}

export default App;
